import {DetailedHTMLProps, HTMLAttributes, ReactElement, useEffect, useRef} from 'react';
import clsx from 'clsx';
import {WebviewUtils} from '@shared/webviewUtils';
import {PopupWindowBody} from '@shared/cci/features/webviewUtils';

export interface BottomSheetWrapperProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  bottomSheetFallbackHeight: number;
  heightOffset?: number;
  children: ReactElement;
}

// 팝업 윈도우의 ResizeObserver를 쓸 수 있도록 전역 window에 선언한다.
let resizeObserver: ResizeObserver;
(window as any).startResizeObserver = (target: Element, callback: ResizeObserverCallback) => {
  resizeObserver = new ResizeObserver(callback);
  resizeObserver.observe(target);
};

(window as any).stopResizeObserver = () => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
};

function BottomSheetWebViewWrapper({bottomSheetFallbackHeight, heightOffset = 0, children, ...props}: BottomSheetWrapperProps) {
  const contentsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const contentsElement = contentsRef.current as Element;
    const target = contentsElement.children[0];

    // 바닥 페이지가 body DOM 엘리먼트에 추가한 현재 팝업의 윈도우를 얻는다.
    // openBottomSheetWebview()의 주석 참고할 것.
    const body = contentsElement.closest('body') as PopupWindowBody;
    const popupWindow = body.popupWindow || window;

    // 팝업 윈도우의 ResizeObserver를 사용한다.
    // 만약 여기서 new ResizeObserver()와 같이 곧바로 생성하여 사용하면
    // 이 코드는 레시컬 스코프에 의해 바닥 페이지의 ResizeObserver가 생성되며
    // 팝업의 요소를 올바르게 추적하지 못한다.
    // 여기서, popupWindow의 startResizeObserver를 호출하면
    // 이 파일 상단에 적은 코드는 팝업 윈도우의 레시컬 스코프를 가지므로
    // 올바르게 동작하게 된다.
    // 정확하게는 바닥 페이지와 팝업 페이지 둘다 startResizeObserver를 가지고 있을 것이고
    // 팝업의 것을 사용하기 위해 명시적으로 popupWindow. 으로 작성하였다
    // 즉, 이 코드는 바닥 페이지의 렉시컬 스코프에서 팝업의 렉시컬 스코프를 호출하는 것이다.
    (popupWindow as any).startResizeObserver(target, () => {
      WebviewUtils.setBottomSheetWebViewSize(getHeight(), popupWindow);
    });

    // 요소의 크기는 getBoundingClientRect()를 사용하였다
    // padding을 포함한 박스의 크기를 구할 수 있는 ResizeObserver entry의 contentBoxSize는 iOS 15.4부터 지원되며
    // 이 프로젝트 최소 지원 범위인 iOS 15.0을 커버하지 못하기 때문에 직접 구한다.
    // (참고, 호환성이 높은 entry의 contentRect는 padding을 포함한 크기를 주지 않는다)
    function getHeight() {
      const height = Math.round(target.getBoundingClientRect().height + heightOffset);
      if (height === 0) {
        return bottomSheetFallbackHeight;
      }

      return height;
    }

    return () => {
      (popupWindow as any).stopResizeObserver();
    };
  }, [bottomSheetFallbackHeight, heightOffset]);

  return (
    <div
      {...props}
      className={`${clsx('bottom_sheet_area', props.className)}`}
      ref={contentsRef}
    >
      {children}
    </div>
  );
}

export default BottomSheetWebViewWrapper;
